import {Fragment, useEffect} from "react";
import * as Realm from "realm-web";
import {BSON} from "realm-web";

const {
    BSON: {ObjectId},
} = Realm;

const APP_ID = "statsheetrebuild-bjyjo";

export class StatSheetRealm {
    app: Realm.App;
    user?: Realm.User;
    sendMessage?: (gameObjectName: string, methodName: string, parameter?: string | number | undefined) => void;
    private indexedDB?: IDBDatabase;
    clipBoardData?: string;

    static persistentSplitter: string = "|*$|";

    constructor() {
        this.app = new Realm.App({id: APP_ID});
    }

    async TryLoginFromCache(): Promise<boolean> {
        // Check if the user ID exists in local storage
        const storedUserId = localStorage.getItem('realmUserId');

        if (storedUserId) {
            // If the user ID exists, try to get the user from the Realm app
            const user = this.app.allUsers[storedUserId];

            if (user) {
                // Set the current user
                try {
                    await user.refreshProfile();
                } catch (e) {
                    console.error("User expired", e);
                    localStorage.removeItem('realmUserId');
                    return false;
                }

                this.user = user;
                return true;
            } else {
                // If the user is not found, remove the stored user ID and create a new user
                localStorage.removeItem('realmUserId');
            }
        }
        return false;
    }

    async LoginInAnonymousCallback(objectName: string, callback: string, fallback: string): Promise<void> {
        // Check if the user ID exists in local storage
        const storedUserId = localStorage.getItem('realmUserId');

        if (storedUserId) {
            // If the user ID exists, try to get the user from the Realm app
            const user = this.app.allUsers[storedUserId];

            if (user) {
                // Set the current user
                try {
                    await user.refreshProfile();
                } catch (e) {
                    console.error("User expired", e);
                    localStorage.removeItem('realmUserId');
                    return await this.LoginInAnonymousCallback(objectName, callback, fallback);
                }

                this.user = user;
                this.sendMessage?.(objectName, callback, JSON.stringify(user));
                return;
            } else {
                // If the user is not found, remove the stored user ID and create a new user
                localStorage.removeItem('realmUserId');
            }
        }


        // Create a new anonymous user
        const newUser = await this.app.logIn(Realm.Credentials.anonymous());

        // Store the new user ID in local storage
        localStorage.setItem('realmUserId', newUser.id);

        this.user = newUser;
        this.sendMessage?.(objectName, callback, JSON.stringify(newUser));
        return;
    };

    async LoginUserPassCallback(objectName: string, callback: string, fallback: string, email: string, password: string) {
        try {
            await this.app.emailPasswordAuth.registerUser({
                email: email,
                password: password,
            });
        } finally {
            const newUser = await this.app.logIn(Realm.Credentials.emailPassword(email, password));
            if (newUser.isLoggedIn) {
                this.user = newUser;
                const obj = JSON.parse(JSON.stringify(newUser));
                obj.address = email;
                if (obj.id) obj.profileId = obj.id;
                const parsed = JSON.stringify(obj);
                this.sendMessage?.(objectName, callback, parsed);
            } else {
                this.sendMessage?.(objectName, fallback, JSON.stringify({}));
            }
        }
    }

    async LoginEmailPasswordCallback(objectName: string, callback: string, fallback: string, email: string, password: string) {
        const newUser = await this.app.logIn(Realm.Credentials.emailPassword(email, password));
        if (newUser.isLoggedIn) {
            this.user = newUser;
            this.sendMessage?.(objectName, callback, JSON.stringify(newUser));
        } else {
            this.sendMessage?.(objectName, fallback, JSON.stringify({}));
        }
    }

    async RegisterEmailPasswordCallback(objectName: string, callback: string, fallback: string, email: string, password: string) {
        try {
            await this.app.emailPasswordAuth.registerUser({
                email: email,
                password: password,
            });
            this.sendMessage?.(objectName, callback, JSON.stringify({}));
        } catch (e) {
            console.error(e);
            this.sendMessage?.(objectName, fallback, JSON.stringify(e));
            return;
        }
    }

    async RealmSaveCallback(objectName: string, callback: string, fallback: string, data: string, collectionName: string) {
        const client = await this.GetMongoClient(collectionName);
        if (!client || !data) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(this.GenerateError("No client or data")));
            return;
        }
        const parsedData = JSON.parse(data);

        const dateKeys = ["createdAt", "updatedAt", "modifiedAt", "tokenMintedAt", "listTime"];

        // Prevent 0 values from being saved as numbers, since they are not supported by Realm
        const numberKeys = ["listPrice"];

        for (const key of dateKeys) {
            if (parsedData.hasOwnProperty(key)) {
                parsedData[key] = new Date(parsedData[key]);
            }
        }

        for (const key of numberKeys) {
            if (parsedData.hasOwnProperty(key)) {
                parsedData[key] = Number(parsedData[key]);
                if (parsedData[key] === 0) {
                    parsedData[key] = Number.EPSILON;
                }
            }
        }

        const TryUpdate = async (id: BSON.ObjectId) => {
            if (parsedData.hasOwnProperty("updatedAt")) {
                parsedData.updatedAt = new Date();
            }
            if (parsedData.hasOwnProperty("modifiedAt")) {
                parsedData.modifiedAt = new Date();
            }

            await client.updateOne(
                {_id: id},
                {
                    $set: parsedData
                });
            /*
            if (collectionName === "RealmStatSheetProfile")
                await client.updateOne(
                    {_id: id},
                    {
                        $set: {
                            profileData: JSON.stringify(parsedData),
                        }
                    });
            else {
                await client.updateOne(
                    {_id: id},
                    {
                        $set: parsedData
                    });
            }*/
        };

        if (parsedData) {
            // This is the root object, so we need to save it by inserting it
            if (parsedData._id && ObjectId.isValid(parsedData._id)) {
                parsedData._id = new ObjectId(parsedData._id);
                // insert a new document if the _id is not found
                const result = await client.find({
                    "_id": parsedData._id,
                });
                if (result.length === 0) {
                    if (parsedData.hasOwnProperty("ownerId")) {
                        parsedData.ownerId = this.user?.id!;
                    }

                    switch (collectionName) {
                        case "RealmStatSheetProfile":
                            break;
                        case "RealmStatSheetItem":
                            if (!parsedData.hasOwnProperty("stars")) parsedData.stars = 1;
                            if (!parsedData.hasOwnProperty("rarity")) parsedData.rarity = 0;
                            if (!parsedData.hasOwnProperty("category")) parsedData.category = null;
                            if (!parsedData.hasOwnProperty("iconReference")) parsedData.iconReference = null;
                            if (!parsedData.hasOwnProperty("iconReference2")) parsedData.iconReference2 = null;
                            if (!parsedData.hasOwnProperty("tokenId")) parsedData.tokenId = null;
                            break;
                        case "RealmStatSheetSpell":
                            if (!parsedData.hasOwnProperty("stars")) parsedData.stars = 1;
                            if (!parsedData.hasOwnProperty("rarity")) parsedData.rarity = 0;
                            if (!parsedData.hasOwnProperty("category")) parsedData.category = null;
                            if (!parsedData.hasOwnProperty("iconReference")) parsedData.iconReference = null;
                            if (!parsedData.hasOwnProperty("iconReference2")) parsedData.iconReference2 = null;
                            if (!parsedData.hasOwnProperty("tokenId")) parsedData.tokenId = null;
                            break;
                    }

                    await client.insertOne(parsedData);
                } else {
                    // otherwise we just update the profileData field with the parsedData
                    await TryUpdate(parsedData._id);
                }
            } else if (parsedData.realmProfileId && ObjectId.isValid(parsedData.realmProfileId)) {
                await TryUpdate(new ObjectId(parsedData.realmProfileId));
            }
        }
        this.sendMessage?.(objectName, callback, JSON.stringify({}));
    }

    async RealmDeleteProfileCallback(objectName: string, callback: string, fallback: string, data: string) {
        const client = await this.GetMongoClient("RealmStatSheetProfile");
        if (!client || !data) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(new Error("Invalid data")));
            return;
        }
        const result = await client.deleteOne(data.length === 36 ? {
            profileId: data,
            ownerId: this.user?.id!
        } : {
            _id: new ObjectId(data),
            ownerId: this.user?.id!
        });
        this.sendMessage?.(objectName, callback, JSON.stringify({}));
    }

    async RealmDeleteItemCallback(objectName: string, callback: string, fallback: string, data: string) {
        const client = await this.GetMongoClient("RealmStatSheetItem");
        if (!client || !data) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(new Error("Invalid data")));
            return;
        }
        const result = await client.deleteOne(data.length === 36 ? {
            guid: data,
            ownerId: this.user?.id! || ""
        } : {
            _id: new ObjectId(data),
            ownerId: this.user?.id! || ""
        });
        this.sendMessage?.(objectName, callback, JSON.stringify({}));
    }

    async RealmDeleteSpellCallback(objectName: string, callback: string, fallback: string, data: string) {
        const client = await this.GetMongoClient("RealmStatSheetSpell");
        if (!client || !data) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(new Error("Invalid data")));
            return;
        }
        const result = await client.deleteOne(data.length === 36 ? {
            guid: data,
            ownerId: this.user?.id! || ""
        } : {
            _id: new ObjectId(data),
            ownerId: this.user?.id! || ""
        });
        this.sendMessage?.(objectName, callback, JSON.stringify({}));
    }

    async GetAllProfilesCallback(objectName: string, callback: string, fallback: string, ownerId: string) {
        const client = await this.GetMongoClient("RealmStatSheetProfile");
        if (!client || !ownerId) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(this.GenerateError("No client or data")));
            return;
        }
        if (client) {
            try {
                const result = await client.find({
                    "ownerId": ownerId,
                });
                this.sendMessage?.(objectName, callback, JSON.stringify(result));
            } catch (e) {
                console.error(e);
                this.sendMessage?.(objectName, fallback, JSON.stringify(e));
            }
        }
    }

    async GetAllItemsCallback(objectName: string, callback: string, fallback: string, ownerId: string) {
        const client = await this.GetMongoClient("RealmStatSheetItem");
        if (!client || !ownerId) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(this.GenerateError("No client or data")));
            return;
        }
        if (client) {
            try {
                const result = await client.find({
                    "ownerId": ownerId,
                });
                this.sendMessage?.(objectName, callback, JSON.stringify(result));
            } catch (e) {
                console.error(e);
                this.sendMessage?.(objectName, fallback, JSON.stringify(e));
            }
        }
    }

    async GetAllSpellsCallback(objectName: string, callback: string, fallback: string, ownerId: string) {
        const client = await this.GetMongoClient("RealmStatSheetSpell");
        if (!client || !ownerId) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(this.GenerateError("No client or data")));
            return;
        }
        if (client) {
            try {
                const result = await client.find({
                    "ownerId": ownerId,
                });
                this.sendMessage?.(objectName, callback, JSON.stringify(result));
            } catch (e) {
                console.error(e);
                this.sendMessage?.(objectName, fallback, JSON.stringify(e));
            }
        }
    }


    timeout(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async InitializeRealmCallback(objectName: string, callback: string, fallback: string) {
        await this.timeout(100);
        this.sendMessage?.(objectName, callback, JSON.stringify({}));
    }

    async SetClipboardCallback(objectName: string, callback: string, fallback: string, data: string) {
        try {
            console.log("Setting clipboard data", data);
            // if the data is a base64 image, we need to convert it to a blob
            const imageRegex = /^data:image\/(png|jpeg|gif|bmp);base64,/;
            if (imageRegex.test(data)) {
                const base64Data = data.replace(imageRegex, '');
                const colonSplit = base64Data.split(":");
                const firstColon = colonSplit[0];
                const type = data.match(imageRegex)?.[1] || 'png';
                console.log("Clipboard data set - image", type, firstColon.length);
                const blob = await fetch(`data:image/${type};base64,${firstColon}`).then((r) => r.blob());
                await navigator.clipboard.write([
                    new ClipboardItem({
                        [blob.type]: blob
                    })
                ]);
            } else {
                console.log("Clipboard data set - text");
                await navigator.clipboard.writeText(data);
            }

            this.sendMessage?.(objectName, callback, JSON.stringify({}));
        } catch (error) {
            console.error('Failed to write to clipboard', error);
            this.sendMessage?.(objectName, fallback, JSON.stringify({error}));
        }
    }

    async GetClipboardCallback(objectName: string, callback: string, fallback: string) {
        const fallbackFunc = () => {
            if (typeof this.clipBoardData === 'string') {
                this.sendMessage?.(objectName, callback, this.clipBoardData);
            } else {
                this.sendMessage?.(objectName, fallback, JSON.stringify({error: 'No clipboard data'}));
            }
        };

        const base64ToUint8Array = (base64: string) => {
            const binaryString = window.atob(base64);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes;
        };

        const processClipboardImage = async (base64: string) => {
            if (base64.startsWith('data:text/html;base64,')) {
                const uint8Array = base64ToUint8Array(base64.replace('data:text/html;base64,', ''));
                const html = new TextDecoder('utf-8').decode(uint8Array);
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const img = doc.querySelector('img');
                if (img && img.src) {
                    console.log("Clipboard image read (html)", img.src);
                    // the src is a link to an image, fetch it and send it to the client
                    const response = await fetch(img.src, {mode: 'cors'});
                    if (!response.ok) {
                        throw new Error(`Network response was not ok: ${response.statusText}`);
                    }
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        if (e.target && e.target.result) {
                            console.log("Clipboard image read (html src)", e.target.result);
                            this.sendMessage?.(objectName, callback, e.target.result as string);
                        } else {
                            console.error('null target in clipboard read', e);
                            fallbackFunc();
                        }
                    };

                    const blob = await response.blob();
                    reader.readAsDataURL(blob);
                } else {
                    fallbackFunc();
                }
            } else {
                this.sendMessage?.(objectName, callback, base64);
            }
        };

        try {
            // Request permission to read the clipboard
            const permissionStatus = await navigator.permissions.query({name: 'clipboard-read'} as any);

            if (permissionStatus.state === 'granted' || permissionStatus.state === 'prompt') {
                // Read text from clipboard
                const text = await navigator.clipboard.readText();
                if (text) {
                    await processClipboardImage(text);
                } else {
                    // Try to read images or other data if text is not available
                    const items = await navigator.clipboard.read();
                    let foundImage = false;
                    console.log("Clipboard items", items);
                    for (let item of items) {
                        console.log("Clipboard item type", item.types);
                        if (item.types.includes('image/png') || item.types.includes('image/jpeg') || item.types.includes('image/gif') || item.types.includes('image/bmp')) {
                            const findType = item.types.find((type) => type.startsWith('image/'));
                            const blob = await item.getType(findType!);
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                if (e.target && e.target.result) {
                                    console.log("Clipboard image read (image)", e.target.result);
                                    processClipboardImage(e.target.result as string);
                                } else {
                                    console.error('null target in clipboard read', e);
                                    fallbackFunc();
                                }
                            };
                            reader.readAsDataURL(blob);
                            foundImage = true;
                            break;
                        }
                    }
                    if (!foundImage) {
                        fallbackFunc();
                    }
                }
            } else {
                this.sendMessage?.(objectName, fallback, JSON.stringify({error: 'Clipboard access denied'}));
            }
        } catch (error) {
            console.error('Error reading clipboard:', error);
            fallbackFunc();
        }
    }


    async RealmRefreshCallback(objectName: string, callback: string, fallback: string) {
        this.sendMessage?.(objectName, callback, JSON.stringify({}));
    }

    async ExecuteFunctionCallback(objectName: string, callback: string, fallback: string, functionName: string, parameter: string) {
        this.sendMessage?.(objectName, callback, JSON.stringify({}));
    }

    async GenerateNewIdCallback(objectName: string, callback: string, fallback: string) {
        const id = ObjectId.generate().toString('hex');
        this.sendMessage?.(objectName, callback, id.toString());
    }

    private GetMongoClient = async (collectionName: string) => {
        if (!this.user) {
            const result = await this.TryLoginFromCache();
            if (!result) {
                return null;
            }
        }

        if (this.user) {
            const mongo = this.user.mongoClient("mongodb-atlas");
            return mongo.db("StatSheetRebuild").collection(collectionName);
        }
        return null;
    }
    private chunkSize: number = 975 * 1024;

    private GenerateError(data: string) {
        return {
            message: data,
        }
    }

    async GoogleIsSignedInCallback(objectName: string, callback: string, fallback: string, data: string) {
        const persistentId = `${StatSheetRealm.persistentSplitter}${data}`;
        const currentWindow = window as any;
        const isSignedIn = false;
        this.sendMessage?.(objectName, callback, JSON.stringify({isSignedIn}) + persistentId);
    }

    async GoogleAuthCallback(objectName: string, callback: string, fallback: string, data: string) {
        const persistentId = `${StatSheetRealm.persistentSplitter}${data}`;
        try {
            const currentWindow = window as any;
            this.sendMessage?.(objectName, callback, JSON.stringify({}) + persistentId);
        } catch (error) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(error) + persistentId);
        }
    }

    async GoogleLogoutCallback(objectName: string, callback: string, fallback: string, data: string) {
        const persistentId = `${StatSheetRealm.persistentSplitter}${data}`;
        try {
            const currentWindow = window as any;
            this.sendMessage?.(objectName, callback, JSON.stringify({}) + persistentId);
        } catch (error) {
            this.sendMessage?.(objectName, fallback, JSON.stringify(error) + persistentId);
        }
    }

    async OpenUrlCallback(objectName: string, callback: string, fallback: string, url: string, download: boolean) {
        console.log("Opening URL", url, download ? "with download" : "without download");
        if (download) {
            try {
                // Fetch the image as a Blob
                const response = await fetch(url, {mode: 'cors'});
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }
                const blob = await response.blob();

                // Create a URL for the Blob
                const blobUrl = URL.createObjectURL(blob);

                // Create an invisible anchor element
                const anchor = document.createElement('a');
                anchor.href = blobUrl;
                anchor.download = url.split('/').pop() || 'download'; // Set the download attribute with a filename
                anchor.style.display = 'none'; // Hide the anchor
                document.body.appendChild(anchor); // Append anchor to the body

                // Create a mouse event to simulate a user click
                const event = new MouseEvent('click', {
                    view: window,
                    bubbles: true,
                    cancelable: true
                });

                anchor.dispatchEvent(event); // Dispatch the event to trigger the download
                document.body.removeChild(anchor); // Remove the anchor from the DOM

                // Revoke the Blob URL after download
                URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error('Error downloading the file:', error);
            }
        } else {
            window.open(url, "_blank");
        }

        this.sendMessage?.(objectName, callback, JSON.stringify({
            url: url,
        }));
    }

    async OpenIndexDBCallback(objectName: string, callback: string, fallback: string) {
        const request = indexedDB.open("StatSheetRebuild", 1);
        request.onerror = (event) => {
            console.error("Error opening database", event);
            this.sendMessage?.(objectName, fallback, JSON.stringify(this.GenerateError("Error opening database")));
        };
        request.onsuccess = (event) => {
            this.indexedDB = request.result;
            if (!this.indexedDB.objectStoreNames.contains("images"))
                this.indexedDB.createObjectStore("images");
            this.sendMessage?.(objectName, callback, JSON.stringify({}));
        };
        request.onupgradeneeded = (event) => {
            const db = request.result;
            if (!db.objectStoreNames.contains("images"))
                db.createObjectStore("images");
        };
    }

    async SaveImageCallback(objectName: string, callback: string, fallback: string, fileName: string, data: string) {
        // wait until this.indexedDB is set
        while (!this.indexedDB) {
            await this.timeout(100);
        }

        const persistentId = `${StatSheetRealm.persistentSplitter}${fileName}`;
        try {
            this.indexedDB?.transaction("images", "readwrite").objectStore("images").put(data, fileName);
            this.sendMessage?.(objectName, callback, JSON.stringify({
                "fileName": fileName,
                "saved": true,
            }) + persistentId);
        } catch (e) {
            this.sendMessage?.(objectName, callback, JSON.stringify({
                "fileName": fileName,
                "saved": false,
            }) + persistentId);
        }
    }

    async DownloadImageCallback(objectName: string, callback: string, fallback: string, fileName: string, data: string) {
        // wait until this.indexedDB is set
        while (!this.indexedDB) {
            await this.timeout(100);
        }

        const persistentId = `${StatSheetRealm.persistentSplitter}${fileName}`;

        console.log("Downloading image", fileName, data.length);

        try {
            // Download the image as a Blob
            const dataUrl = `data:image/png;base64,${data}`;
            console.log("pre-fetching image", dataUrl);
            const response = await fetch(dataUrl);
            console.log("fetched image", response);

            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Create an invisible anchor element
            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            anchor.download = fileName;
            anchor.style.display = 'none';
            document.body.appendChild(anchor);

            // Create a mouse event to simulate a user click
            const event = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true
            });

            anchor.dispatchEvent(event);
            document.body.removeChild(anchor);
            URL.revokeObjectURL(blobUrl);

            console.log("Done downloading image", fileName);

            this.sendMessage?.(objectName, callback, JSON.stringify({
                "fileName": fileName,
                "saved": true,
            }) + persistentId);
        } catch (e) {
            this.sendMessage?.(objectName, callback, JSON.stringify({
                "fileName": fileName,
                "saved": false,
            }) + persistentId);
        }
    }

    async LoadImageCallback(objectName: string, callback: string, fallback: string, fileName: string, chunkIndex: string) {
        // wait until this.indexedDB is set
        while (!this.indexedDB) {
            await this.timeout(100);
        }

        const persistentId = `${StatSheetRealm.persistentSplitter}${fileName}:${chunkIndex}`;
        const parsedChunkIndex = parseInt(chunkIndex);
        const request = this.indexedDB?.transaction("images", "readwrite").objectStore("images").get(fileName);
        if (request) {
            request.onerror = (event) => {
                console.error("Error loading image", event);
                this.sendMessage?.(objectName, callback, JSON.stringify({}) + persistentId);
            };
            request.onsuccess = (event) => {
                if (event.target && request.result) {
                    const base64ImageData = request.result;
                    const maxChunkSize = this.chunkSize;

                    // Calculate the number of chunks required to split the base64 string
                    const chunkCount = Math.ceil(base64ImageData.length / maxChunkSize);

                    // Split the base64 string into smaller chunks and create JSON objects
                    for (let i = 0; i < chunkCount; i++) {
                        if (parsedChunkIndex !== i) {
                            continue;
                        }
                        const chunkStart = i * maxChunkSize;
                        const chunkEnd = Math.min((i + 1) * maxChunkSize, base64ImageData.length);
                        const imageDataChunk = base64ImageData.slice(chunkStart, chunkEnd);

                        const jsonChunk = JSON.stringify({
                            "imageData": imageDataChunk,
                            "fileName": fileName,
                            "chunkIndex": i,
                            "totalChunks": chunkCount
                        });

                        // Send the JSON object using sendMessage
                        this.sendMessage?.(objectName, callback, jsonChunk + persistentId);
                    }
                }
            };
        } else {
            this.sendMessage?.(objectName, callback, JSON.stringify({}) + persistentId);
        }
    }

    async HasImageCallback(objectName: string, callback: string, fallback: string, fileName: string) {
        // wait until this.indexedDB is set
        while (!this.indexedDB) {
            await this.timeout(100);
        }

        const persistentId = `${StatSheetRealm.persistentSplitter}${fileName}`;
        const request = this.indexedDB?.transaction("images", "readwrite").objectStore("images").get(fileName);
        if (request) {
            request.onerror = (event) => {
                this.sendMessage?.(objectName, callback, JSON.stringify({
                    "hasImage": false,
                    "fileName": fileName,
                    "totalChunks": 0,
                }) + persistentId);
            };
            request.onsuccess = (event) => {
                const maxChunkSize = this.chunkSize; // 500 KB
                const chunkCount = request.result ? Math.ceil(request.result.length / maxChunkSize) : 0;
                this.sendMessage?.(objectName, callback, JSON.stringify({
                    "hasImage": !!request.result,
                    "fileName": fileName,
                    "totalChunks": chunkCount,
                }) + persistentId);
            };
        } else {
            this.sendMessage?.(objectName, callback, JSON.stringify({
                "hasImage": false,
                "fileName": fileName,
                "totalChunks": 0,
            }) + persistentId);
        }
    }
}